import { ModelResource } from './resource';
import { Attachment, Chat, ChatUserMessage } from '../models';

interface CreateMessageEventStream {
  type: 'start' | 'response' | 'end' | 'error' | 'tool_start' | 'tool_end' | 'reasoning';
  message?: Record<string, any>;
  content?: string;
  error?: string;
  tool_name?: string;
}

export class Chats extends ModelResource<Chat> {
  model = Chat;

  resourcePath = 'v1/chats';

  async *createMessage(
    chat: Chat,
    content: string,
    attachments?: Attachment[],
  ): AsyncGenerator<CreateMessageEventStream> {
    const response = await this.api.post(this.path(chat.id, 'messages'), {
      data: {
        content: content,
        attachments: attachments?.map((att) => att.id),
      },
      adapter: 'fetch',
      responseType: 'stream',
      timeout: 120000,
    });

    if (!response.data) throw new Error('ReadableStream not supported');

    const reader = response.data.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.trimEnd().split('\n');

      buffer = '';

      for (const line of lines) {
        if (!line) continue;
        try {
          const parsed: CreateMessageEventStream = JSON.parse(line);
          yield parsed;
        } catch (e) {
          console.error(e);
          buffer = line;
        }
      }

      if (done) break;
    }
  }

  async listMessages(chat: Chat, refresh: boolean = false): Promise<ChatUserMessage[]> {
    let params = {};
    if (!refresh) {
      if (chat.messages?.length) {
        params = { before: chat.messages[chat.messages.length - 1].id };
      }
    }
    const response = await this.api.get(this.path(chat.id, 'messages'), {
      params,
    });

    const messages = response.data.data.map((msg) => ChatUserMessage.fromJson(msg));
    if (refresh) {
      chat.messages = messages;
    } else {
      chat.messages.push(...messages);
    }

    return messages;
  }

  async submitFeedback(
    chatId: string,
    messageId: string,
    data: { sentiment: 'positive' | 'negative'; comment?: string },
  ): Promise<void> {
    await this.api.post(this.path(chatId, 'messages', messageId, 'feedback'), {
      data,
    });
  }

  async deleteFeedback(chatId: string, messageId: string): Promise<void> {
    await this.api.delete(this.path(chatId, 'messages', messageId, 'feedback'));
  }
}
